import { useEffect, useRef, type ReactNode, type RefObject } from "react";

import styles from "./AccessPopover.module.css";

import type { RoleSummary } from "@/types/access";

interface AccessPopoverProps {
  /** The section/page title, for accessible labels. */
  title: string;
  roles: RoleSummary[];
  checkedIds: string[];
  /** `${ownerId}:${roleId}` while that checkbox's request is in flight. */
  busyKey: string | null;
  /** The section/page id the checkboxes belong to. */
  ownerId: string;
  /** Label for the unrestricted state ("Everyone signed in" for sections,
   *  "Everyone who can see this section" for pages). */
  everyoneLabel: string;
  /** The button that opened the popover — presses on it don't count as outside. */
  anchorRef: RefObject<HTMLButtonElement | null>;
  /** Structural actions (rename/delete) rendered under the access list. */
  footer?: ReactNode;
  onToggle: (roleId: string) => void;
  /** Lift every restriction (back to the everyone state) in one write. */
  onClear: () => void;
  onClose: (reason: "escape" | "outside") => void;
}

/**
 * The "who can see this" popover behind each sidebar row's manage button.
 * Two explicit states, radio-style, so the default is never implied by an
 * empty checklist (unticked boxes used to read as "these roles can't see
 * it"): "everyone" (no restriction rows) or "only these roles" with the
 * checklist. The state derives from checkedIds — ticking a role restricts,
 * choosing everyone clears every tick in one write. Closes on Escape or any
 * pointer press outside; the opener restores focus.
 */
export function AccessPopover({
  title,
  roles,
  checkedIds,
  busyKey,
  ownerId,
  everyoneLabel,
  anchorRef,
  footer,
  onToggle,
  onClear,
  onClose,
}: AccessPopoverProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollIntoView?.({ block: "nearest" });
    el.querySelector("input")?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose("escape");
      }
    };
    const onPointerDown = (e: PointerEvent) => {
      if (!(e.target instanceof Node)) return;
      if (ref.current?.contains(e.target)) return;
      // Presses on the opener toggle it closed via its own click handler.
      if (anchorRef.current?.contains(e.target)) return;
      onClose("outside");
    };
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [onClose, anchorRef]);

  return (
    <div
      ref={ref}
      className={styles.popover}
      role="dialog"
      aria-label={footer ? `Manage ${title}` : `Access for ${title}`}
    >
      <div className={styles.title}>Who can see this</div>
      {(() => {
        const restricted = checkedIds.length > 0;
        // Disable all inputs while any write for this owner is in flight:
        // each change sends the complete role list, so a second change fired
        // mid-request would compute from stale state and its PUT would
        // clobber the first (lost update on the ACL).
        const busy = busyKey?.startsWith(`${ownerId}:`) ?? false;
        return (
          <div role="radiogroup" aria-label={`Who can see ${title}`}>
            <label className={styles.item}>
              <input
                type="radio"
                name={`access-mode-${ownerId}`}
                checked={!restricted}
                disabled={busy}
                aria-label={`${everyoneLabel} can see ${title}`}
                onChange={() => {
                  if (restricted) onClear();
                }}
              />
              <span>{everyoneLabel}</span>
            </label>
            <label className={styles.item}>
              <input
                type="radio"
                name={`access-mode-${ownerId}`}
                checked={restricted}
                disabled={busy}
                aria-label={`Only ticked roles can see ${title}`}
                // Derived state: restricting happens by ticking a role below.
                onChange={() => undefined}
              />
              <span>Only these roles:</span>
            </label>
            <div
              className={`${styles.list} ${styles.roleList} ${restricted ? "" : styles.roleListMuted}`}
              role="group"
              aria-label={`Roles allowed to see ${title}`}
            >
              {roles.map((role) => {
                const checked = checkedIds.includes(role.id);
                return (
                  <label key={role.id} className={styles.item}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={busy}
                      aria-label={`${role.name} can see ${title}`}
                      onChange={() => onToggle(role.id)}
                    />
                    <span>{role.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })()}
      {checkedIds.length > 0 && (
        <p className={styles.hint}>
          Hidden from everyone else — in OpsHub navigation, search, and chat.
        </p>
      )}
      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  );
}
